import React, { useEffect, useState, useRef } from "react";
import "./CreateVendorForm.scss";
import CustomQuestionForm from "../../components/CustomQuestionForm/CustomQuestionForm";
import VendorFormReview from "../../components/VendorFormReview/VendorFormReview";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { QuestionSchema, UpdateFormPayload } from "../../models/QuestionForm";
import ApiService from "../../services/apiService";
import URLS from "../../urls/Urls";
import { toast } from "react-toastify";
import { toastOptions } from "../../utils/toast";

interface VendorFormProps {
  active?: number;
  update?: boolean;
  vendorFormId?: number;
}

interface IVendorDetail {
  created_at: string;
  description: string;
  id: number;
  published: false;
  questions: QuestionSchema;
  title: string;
}

const CreateVendorForm: React.FC<VendorFormProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { id } = useParams();
  const [newQuestionId, setNewQuestionId] = useState(-1);
  const [activeStep, setActiveStep] = useState(id ? 1 : 1);
  const [formTitle, setFormTitle] = useState("");
  const [published, setPublished] = useState(false);
  const [formDescription, setFormDescription] = useState("");
  const [allQuestions, setAllQuestions] = useState<QuestionSchema[]>([]);
  const [oldVendorDetail, setOldVendorDetails] =
    useState<IVendorDetail | null>();
  const [updateFormPayload, setUpdateFormPayload] = useState<UpdateFormPayload>(
    {
      edit: [],
      delete: [],
    },
  );
  const containerRef = useRef<HTMLDivElement>();

  useEffect(() => {
    if (id) {
      getVendorFormdetail(parseInt(id));
      setFormUpdatePayload();
    }
  }, []);

  const setFormUpdatePayload = () => {};

  const getVendorFormdetail = async (vendorFormId: number) => {
    const { data } = await ApiService().client.get(
      URLS.FORMS.GET_VENDOR_FORM_DETAIL(Number(vendorFormId)),
    );
    setOldVendorDetails(data.response);
    setFormTitle(data.response.title);
    setFormDescription(data.response.description);
    setAllQuestions(data.response.questions || []);
    setPublished(data.response.published);
  };

  const handleAddNewQuestion = (inputType: string) => {
    const newQuestion = {
      id: newQuestionId,
      input_type: inputType,
      question_value: "",
      isRequired: false,
      options: [],
    };
    setAllQuestions((prev) => [...prev, newQuestion]);
    setUpdateFormPayload((prev) => {
      return {
        ...prev,
        edit: [...prev.edit, newQuestion],
      };
    });

    setNewQuestionId((prevId) => prevId - 1);
  };

  const handleDeleteQuestion = (questionId: number) => {
    setAllQuestions((prevQuestions) =>
      prevQuestions.filter((question) => question.id !== questionId),
    );
    setUpdateFormPayload((prev) => {
      return {
        edit: prev.edit.filter((question) => question.id !== questionId),
        delete: [...prev.delete, questionId],
      };
    });
  };

  const handleQuestionValueChanges = (
    id: number,
    newValues: QuestionSchema,
  ) => {
    setAllQuestions((prev) =>
      prev.map((question) => {
        if (question.id === newValues.id) {
          return newValues;
        }
        return question;
      }),
    );
    setUpdateFormPayload((prev) => {
      const index = prev.edit.map((row) => row.id).indexOf(id);
      let newEdit = [...prev.edit];
      if (index === -1) {
        newEdit = [...newEdit, newValues];
      } else {
        newEdit[index] = newValues;
      }
      return {
        ...prev,
        edit: newEdit,
      };
    });
  };

  const payloadConversion = async (questions: QuestionSchema[]) => {
    return questions.map((question) => {
      const options = question.options?.map((opt) => opt.name) || [];

      return {
        question_id: question.id,
        question: question.question_value,
        input_type: question.input_type,
        options: options,
        required: question.isRequired,
      };
    });
  };

  const handleSubmitData = async (saveAsDraft: boolean) => {
    try {
      const questions = await payloadConversion(allQuestions);

      const { data } = await ApiService().client.post(
        URLS.FORMS.CREATE_VENDOR_FORM,
        {
          title: formTitle,
          description: formDescription,
          questions: questions,
          form_status: saveAsDraft ? "DRAFT" : "PUBLISHED",
          published: saveAsDraft ? false : true,
        },
      );
      navigate(`/forms/${data.data.id}`);
    } catch (error) {
      console.error("Error submitting form", error);
    }
  };

  const handleUpdateForm = async (saveAsDraft: boolean) => {
    try {
      if (id) {
        if (oldVendorDetail && oldVendorDetail.title !== formTitle) {
          updateFormPayload.title = formTitle;
        }
        if (
          oldVendorDetail &&
          oldVendorDetail.description !== formDescription
        ) {
          updateFormPayload.description = formDescription;
        }
        if (!saveAsDraft) {
          updateFormPayload.published = true;
        }

        await ApiService().client.put(
          URLS.FORMS.UPDATE_VENDOR_FORM(parseInt(id)),
          updateFormPayload,
        );
        navigate(`/forms/${id}`);
      }
    } catch (error) {
      console.error("Error updating form", error);
    }
  };

  const handleBack = () => {
    if (activeStep > 1) {
      if (id && activeStep === 1) {
        navigate("/forms");
        return;
      }
      setActiveStep(activeStep - 1);
      containerRef?.current?.scroll({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleNext = () => {
    if (formTitle.length === 0) {
      toast.error("Please enter the Title", toastOptions);
      return;
    }
    if (formDescription.length === 0) {
      toast.error("Please enter the Description", toastOptions);
      return;
    }
    if (allQuestions.length == 0) {
      toast.error("Please Add the Question", toastOptions);
      return;
    }
    let questionIncomplete;
    allQuestions.map((quest, index) => {
      if (quest.question_value.length <= 0) {
        questionIncomplete = true;
        return;
      }
    });
    if (questionIncomplete) {
      toast.error("Please Complete the Questions", toastOptions);
      return;
    }

    if (activeStep < 2) {
      setActiveStep(activeStep + 1);
      containerRef?.current?.scroll({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  return (
    <div className="create-vendor-form-page flex-column full-width ">
      {id == null && (
        <div className="upper-bar">
          <p
            onClick={() => navigate("/forms")}
            className="xetgo-font-tag bold px-16 py-12 cursor-pointer"
          >
            Form <span style={{ color: "#7B7D84" }}>/ New </span>
          </p>
        </div>
      )}
      <div className="vendor-main-form-container full-width flex-column flex-1">
        <div className="xet-form-upper-container flex-row align-items-center justify-content-center px-27 py-14 gap-16">
          <div className="left-box flex-row gap-16 align-items-center justify-content-center">
            <div className="step-detail-container flex-row gap-8 xetgo-font-tag">
              <p
                className={`form-step flex-row align-items-center justify-content-center p-2 border-box ${
                  activeStep === 1 && "form-step-active"
                }`}
              >
                {id ? 1 : 1}
              </p>
              <p className={`${activeStep === 1 && "title-active"}`}>
                Add Custom Questions
              </p>
            </div>
            <img
              height={32}
              width={10}
              src={
                activeStep === 1
                  ? "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240826093549-Vector+2.svg"
                  : "https://d2k6zobmg5lufr.cloudfront.net/assets%2F20240826093549-Vector+3.svg"
              }
              alt="side-arrow"
            />
            <div className="step-detail-container flex-row gap-8 xetgo-font-tag">
              <p
                className={`form-step flex-row align-items-center justify-content-center p-2 border-box ${
                  activeStep === 2 && "form-step-active"
                }`}
              >
                {id ? 2 : 2}
              </p>
              <p className={`${activeStep === 2 && "title-active"}`}>
                Review & Publish
              </p>
            </div>
          </div>
        </div>
        <div
          className="component-wrapper-container flex-column flex-1 p-16  border-box"
          id="vendor-create-form-container"
        >
          {activeStep === 1 && (
            <CustomQuestionForm
              allQuestions={allQuestions}
              handleAddNewQuestion={handleAddNewQuestion}
              handleDeleteQuestion={handleDeleteQuestion}
              handleQuestionValueChanges={handleQuestionValueChanges}
              formTitle={formTitle}
              setFormTitle={setFormTitle}
              formDescription={formDescription}
              setFormDescription={setFormDescription}
              vendorForm={true}
            />
          )}
          {activeStep === 2 && (
            <VendorFormReview
              allQuestions={allQuestions}
              formTitle={formTitle}
              formDescription={formDescription}
            />
          )}
        </div>
        <div
          className={`buttons-container flex-row gap-12   align-items-center xetgo-font-tag ${
            activeStep == 1
              ? " justify-content-end"
              : "justify-content-space-between"
          }`}
        >
          {activeStep != 1 && (
            <div
              onClick={handleBack}
              className="back-btn cursor-pointer py-8 flex-row align-items-center justify-content-center"
            >
              Back
            </div>
          )}
          {activeStep === 2 ? (
            <div className="flex-row align-items-center gap-8">
              {!published && (
                <div
                  onClick={
                    id
                      ? () => handleUpdateForm(true)
                      : () => handleSubmitData(true)
                  }
                  className="draft-btn back-btn  cursor-pointer py-12 flex-row align-items-center justify-content-center"
                >
                  Save
                </div>
              )}
              <div
                onClick={
                  id
                    ? () => handleUpdateForm(false)
                    : () => handleSubmitData(false)
                }
                className="back-btn next-btn cursor-pointer py-12 flex-row align-items-center justify-content-center"
              >
                Publish
              </div>
            </div>
          ) : (
            <div
              onClick={handleNext}
              className={`back-btn next-btn cursor-pointer flex-row align-items-center justify-content-center py-8 ${
                formTitle.length === 0 &&
                formDescription.length === 0 &&
                allQuestions.length > 0 &&
                allQuestions[0].question_value?.length <= 0 &&
                activeStep == 1 &&
                "inactive-next"
              }`}
            >
              Next
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateVendorForm;
